// Roles & access: platform structure, seed data and access rules for Domain Setup.
// Modules and pages mirror the profile left navigation (components/ProfileLeftNavBar/helper.js)
// so the labels here always match what users see in the platform.
// TODO: DEFAULT_DOMAIN is seed data; load and save it through the platform API once it exists.
import { moduleGroups, settingsRoutes } from '../../../../components/ProfileLeftNavBar/helper';
import L from './labels';

var languageFile = require('../../../../../public/languages/Translation');

// Nav labels are plain strings, JSX (e.g. RealityCheck<sup>™</sup>) or language-file keys (Settings pages).
const textOf = (node) => {
	if (node == null || node === false) return '';
	if (typeof node === 'string' || typeof node === 'number') return String(node);
	if (Array.isArray(node)) return node.map(textOf).join('');
	return textOf(node.props && node.props.children);
};

const navLabel = (label) => {
	const strings = languageFile[localStorage.getItem('language')] || languageFile.english;
	if (typeof label === 'string' && strings && strings[label]) {
		return strings[label];
	}
	return textOf(label);
};

export const LEVELS = [
	{ value: 0, key: 'hidden', ...L.levels.hidden },
	{ value: 2, key: 'view', ...L.levels.view },
	{ value: 3, key: 'edit', ...L.levels.edit },
];

// Modules come from the left nav module groups; a module with no sub tabs (Calendar) is one page.
// Settings is appended from the left nav settings routes.
export const CATALOG = moduleGroups
	.map((mod) => ({
		id: mod.key,
		name: navLabel(mod.label),
		// Single-page module in the left nav (e.g. Calendar): no sub pages to list or expand.
		single: mod.children.length === 0,
		pages: mod.children.length
			? mod.children.filter((c) => !c.hidden).map((c) => ({ id: c.key, name: navLabel(c.label) }))
			: [{ id: mod.key, name: navLabel(mod.label) }],
	}))
	.concat([{
		id: 'settings',
		name: L.settingsModule,
		pages: settingsRoutes.map((s) => ({ id: s.key, name: navLabel(s.label) })),
	}]);

// Build an access map { pageId: level } from a function (module, page) => level.
export const accessFrom = (fn) => {
	const out = {};
	CATALOG.forEach((m) => m.pages.forEach((p) => { out[p.id] = fn(m, p); }));
	return out;
};

export const DEFAULT_DOMAIN = {
	// Pages this domain includes. Anything false never reaches a profile.
	enabled: accessFrom(() => true),
	defaultRoles: [
		{
			id: 'owner', ...L.defaultRoles.owner, locked: true,
			access: accessFrom(() => 3),
		},
		{
			id: 'admin', ...L.defaultRoles.admin, locked: true,
			access: accessFrom((m, p) => (p.id === 'settings-integrations' ? 2 : p.id === 'settings-payment' ? 0 : 3)),
		},
		{
			id: 'readonly', ...L.defaultRoles.readonly, locked: true,
			access: accessFrom((m, p) => (p.id === 'settings-payment' ? 0 : 2)),
		},
	],
};

// navigationSettings / accessSettings keys → catalog ids.
// Modules map to an object of page flags; a single-page module (Calendar) maps to one boolean.
// `key` is the module key the APIs use now (camelCase, e.g. getDefaultRoles); `legacyKey` is the
// old upper-case key, still read as a fallback but never sent.
export const NAVIGATION_SETTINGS_KEYS = {
	'local-listings': {
		key: 'localListings',
		legacyKey: 'LOCAL_LISTINGS',
		pages: {
			locations: 'LISTING_MANAGEMENT',
			realitycheck: 'REALITY_CHECK',
			performance: 'LISTING_PERFORMANCE',
			'image-management': 'MEDIA_MANAGEMENT',
			'google-audit': 'AUDIT_LOGS',
			'edit-history': 'EDIT_HISTORY',
			'source-mapping': 'SOURCE_MAPPING',
		},
	},
	reputation: {
		key: 'reputation',
		legacyKey: 'REPUTATION',
		pages: {
			reviews: 'REVIEWS_INBOX',
			'rep-dashboard': 'REVIEWS_DASHBOARD',
			'location-ratings': 'LOCATION_RATINGS',
			'sentiment-analysis': 'SENTIMENT_ANALYSIS',
			autoresponse: 'AUTO_RESPONSE',
			competitor: 'COMPETITOR_REVIEWS',
			'review-settings': 'REVIEW_CONTROL',
		},
	},
	'local-social': {
		key: 'localSocial',
		legacyKey: 'LOCAL_SOCIAL',
		pages: {
			posts: 'POSTS_AND_SHOWCASES',
			widgetshowcase: 'REVIEW_SHOWCASE',
		},
	},
	reporting: {
		key: 'reporting',
		legacyKey: 'REPORTING',
		pages: {
			'report-management': 'REPORTS_AND_NOTICES',
			'data-downloads': 'DATA_STUDIO',
		},
	},
	'ai-automations': {
		key: 'aiAndAutomations',
		legacyKey: 'AI_AND_AUTOMATIONS',
		pages: {
			'manage-ai-agents': 'AI_AGENTS',
			automations: 'AUTOMATIONS',
		},
	},
	calendar: { key: 'calendar', legacyKey: 'CALENDAR' },
	settings: {
		key: 'settings',
		legacyKey: 'SETTINGS',
		pages: {
			'settings-profile': 'PROFILE_SETTINGS',
			'settings-integrations': 'INTEGRATIONS',
			'settings-payment': 'BILLING',
			'settings-inviteUser': 'MANAGE_USERS',
			'settings-manageRoles': 'MANAGE_ROLES',
			'settings-manageTeams': 'MANAGE_TEAMS',
			'settings-customNavigation': 'NAVIGATION_CONTROL',
			'settings-filterComposer': 'FILTER_COMPOSER',
			'settings-savedTagsAndFilters': 'SAVED_TAGS_AND_FILTERS',
			'settings-claimLocation': 'CLAIM_LOCATION',
		},
	},
};

// One module's value in a navigationSettings / accessSettings object: the current key, else the legacy one.
const moduleSetting = (settings, map) =>
	settings[map.key] !== undefined ? settings[map.key] : settings[map.legacyKey];

// Is one left-nav page (child key, single-page module key or settings key) switched on for the
// domain? Anything the response doesn't mention, or a key with no mapping, counts as on.
export const isNavPageEnabled = (navigationSettings, pageId) => {
	if (!navigationSettings || !pageId) return true;
	const moduleId = Object.keys(NAVIGATION_SETTINGS_KEYS).find((id) => {
		const map = NAVIGATION_SETTINGS_KEYS[id];
		return map.pages ? pageId in map.pages : id === pageId;
	});
	if (!moduleId) return true;
	const map = NAVIGATION_SETTINGS_KEYS[moduleId];
	const value = moduleSetting(navigationSettings, map);
	if (!map.pages) return value !== false;
	return !(value && value[map.pages[pageId]] === false);
};

// The user's access level for one left-nav page from getProfilesV2 `accessSettings`
// (same shape as navigationSettings, with "EDIT" | "VIEW" | "HIDDEN" per page).
// Returns null when the response doesn't mention the page or the key has no mapping.
export const navPageAccessLevel = (accessSettings, pageId) => {
	if (!accessSettings || !pageId) return null;
	const moduleId = Object.keys(NAVIGATION_SETTINGS_KEYS).find((id) => {
		const map = NAVIGATION_SETTINGS_KEYS[id];
		return map.pages ? pageId in map.pages : id === pageId;
	});
	if (!moduleId) return null;
	const map = NAVIGATION_SETTINGS_KEYS[moduleId];
	const moduleValue = moduleSetting(accessSettings, map);
	const value = map.pages ? moduleValue && moduleValue[map.pages[pageId]] : moduleValue;
	return typeof value === 'string' ? value.toUpperCase() : null;
};

// Can the user see this page? Only "HIDDEN" hides it; EDIT and VIEW both show it.
// Pages the response doesn't mention stay visible, like isNavPageEnabled.
export const isNavPageAccessible = (accessSettings, pageId) =>
	navPageAccessLevel(accessSettings, pageId) !== 'HIDDEN';

// navigationSettings → domain ceiling { pageId: bool }. Anything the response doesn't mention stays on.
export const enabledFromNavigationSettings = (navigationSettings) =>
	accessFrom((m, p) => isNavPageEnabled(navigationSettings, p.id));

// Domain ceiling → navigationSettings, in the shape integrations/read returns.
export const navigationSettingsFromEnabled = (enabled) => {
	const out = {};
	CATALOG.forEach((m) => {
		const map = NAVIGATION_SETTINGS_KEYS[m.id];
		if (!map) return;
		if (!map.pages) {
			out[map.key] = m.pages.some((p) => enabled[p.id] !== false);
			return;
		}
		out[map.key] = {};
		Object.keys(map.pages).forEach((pageId) => {
			out[map.key][map.pages[pageId]] = enabled[pageId] !== false;
		});
	});
	return out;
};

// Domain Setup data for this domain: the API page flags plus the seed default roles.
export const domainFromNavigationSettings = (navigationSettings, defaultRoles) => ({
	...DEFAULT_DOMAIN,
	enabled: enabledFromNavigationSettings(navigationSettings),
	defaultRoles: defaultRoles && defaultRoles.length ? defaultRoles : DEFAULT_DOMAIN.defaultRoles,
});

/* ---------- getDefaultRoles response → Domain Setup roles ---------- */
// Response: { roleCount, navigationSettings, roles: [{ role, roleName, roleId, description, domainName, accessSettings }] }.
// accessSettings has the navigationSettings shape (camelCase module keys) with "EDIT" | "VIEW" | "HIDDEN" per page.

const API_LEVELS = { EDIT: 3, VIEW: 2, HIDDEN: 0 };

// Also used by the Manage Roles table to show a role's Navigation box.
export const accessFromApi = (accessSettings) => {
	const raw = accessSettings || {};
	return accessFrom((m, p) => {
		const map = NAVIGATION_SETTINGS_KEYS[m.id];
		if (!map) return 0;
		const moduleValue = moduleSetting(raw, map);
		const value = map.pages ? moduleValue && moduleValue[map.pages[p.id]] : moduleValue;
		return API_LEVELS[value] || 0;
	});
};

// Built-in role names (matched as "OWNER", "Owner", "Read Only", "READ_ONLY"…) → this page's labels.
// Only used to label an enum-style name.
const API_ROLE_LABELS = {
	OWNER: L.defaultRoles.owner,
	ADMINISTRATOR: L.defaultRoles.admin,
	READ_ONLY: L.defaultRoles.readonly,
};
const roleKey = (s) => s.trim().toUpperCase().replace(/[\s-]+/g, '_');
const titleCase = (s) => s.toLowerCase().split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
// "READ_ONLY"-style enum names are turned into a label; anything else is shown exactly as the API sends it.
const isEnumName = (s) => /^[A-Z0-9_]+$/.test(s);

// Built-in roles (Owner, Administrator, Read Only): getDefaultRoles marks them with roleId "DEFAULT";
// older responses used type "DEFAULT".
export const isBuiltInApiRole = (r) =>
	!!r && (String(r.type || '').toUpperCase() === 'DEFAULT' || r.roleId === 'DEFAULT');

const roleFromApi = (r) => {
	if (!r || !r.roleName) return null;
	const label = API_ROLE_LABELS[roleKey(r.roleName)];
	const enumName = isEnumName(r.roleName);
	return {
		// Built-in roles all come back with roleId "DEFAULT", so key them by roleName.
		id: r.roleId && r.roleId !== 'DEFAULT' ? r.roleId : r.roleName,
		roleId: r.roleId,
		roleName: r.roleName,
		name: !enumName ? r.roleName : label ? label.name : titleCase(r.roleName),
		desc: r.description || '',
		// Default roles can't be edited or deleted.
		locked: isBuiltInApiRole(r),
		access: accessFromApi(r.accessSettings),
		// Original API object, so fields this page doesn't edit go back unchanged on save.
		api: r,
	};
};

export const defaultRolesFromApi = (response) =>
	((response && response.roles) || []).map(roleFromApi).filter(Boolean);

/* ---------- Domain Setup roles → updateDefaultRoles payload ---------- */

const API_LEVEL_NAMES = { 3: 'EDIT', 2: 'VIEW', 0: 'HIDDEN' };

// { pageId: level } → accessSettings in the navigationSettings shape.
// Also used by Manage Roles to build createRole's customRole.accessSettings.
export const accessToApi = (access) => {
	const out = {};
	CATALOG.forEach((m) => {
		const map = NAVIGATION_SETTINGS_KEYS[m.id];
		if (!map) return;
		if (!map.pages) {
			out[map.key] = API_LEVEL_NAMES[access[m.pages[0].id] || 0];
			return;
		}
		out[map.key] = {};
		Object.keys(map.pages).forEach((pageId) => {
			out[map.key][map.pages[pageId]] = API_LEVEL_NAMES[access[pageId] || 0];
		});
	});
	return out;
};

// Roles loaded from the API keep their roleId/roleName/type; roles added on this page send only
// what the user entered, and the API assigns the rest.
export const rolesToApi = (roles, domainName) =>
	roles.map((r) => {
		const api = r.api || {};
		const loaded = api.roleName ? roleFromApi(api) : null;
		const name = r.name.trim();
		const desc = r.desc || '';
		return {
			...api,
			domainName,
			// The page shows API names as labels (OWNER → "Owner") and fills empty descriptions with
			// a default. Send the API's own values back unless the user actually changed them.
			roleName: loaded && name === loaded.name ? api.roleName : name,
			description: loaded && desc === loaded.desc ? api.description || '' : desc,
			accessSettings: accessToApi(r.access),
		};
	});

export const ROLE_NAME_MAX = 20;
export const ROLE_DESC_MAX = 100;
// Most default roles a domain can have (built-in roles included).
export const DEFAULT_ROLES_MAX = 10;

export const clone = (o) => JSON.parse(JSON.stringify(o));

export const uid = (prefix) => `${prefix || 'id'}_${Math.random().toString(36).slice(2, 8)}`;

// Modules + pages visible under the domain ceiling. Disabled pages and empty modules are dropped.
export const scopedCatalog = (domain) =>
	CATALOG.map((m) => ({
		...m,
		pages: m.pages.filter((p) => !domain || domain.enabled[p.id] !== false),
	})).filter((m) => m.pages.length > 0);

// Clamp an access map to the domain ceiling. Pages the domain excludes are always Hidden.
export const clampAccess = (access, domain) => {
	const out = {};
	CATALOG.forEach((m) => m.pages.forEach((p) => {
		const lvl = access && access[p.id] != null ? access[p.id] : 0;
		out[p.id] = domain && domain.enabled[p.id] === false ? 0 : lvl;
	}));
	return out;
};

export const moduleStat = (module, access) => {
	const s = { total: module.pages.length, counts: [0, 0, 0, 0] };
	module.pages.forEach((p) => { s.counts[access[p.id] || 0]++; });
	s.open = s.total - s.counts[0];
	s.uniform = null;
	for (let i = 0; i < 4; i++) if (s.counts[i] === s.total) s.uniform = i;
	return s;
};

export const modulePhrase = (module, access) => {
	const s = moduleStat(module, access);
	if (s.counts[0] === s.total) return L.access.hiddenEntirely;
	if (module.single) return s.uniform === 3 ? L.access.singleEdit : L.access.singleView;
	if (s.uniform === 3) return L.access.editAll(s.total);
	if (s.uniform === 2) return L.access.viewAll(s.total);
	return [3, 2, 0]
		.filter((l) => s.counts[l])
		.map((l) => L.access.mixedPart(s.counts[l], LEVELS.find((lv) => lv.value === l).label))
		.join(L.access.mixedSeparator);
};

export const accessTally = (modules, access) => {
	let total = 0;
	let open = 0;
	modules.forEach((m) => m.pages.forEach((p) => { total++; if (access[p.id] > 0) open++; }));
	return L.access.tally(open, total);
};
